import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  GetEmailSubscriptionsListType,
  GetPlatformeSubscriptionsListType,
  GetUnacknowledgedPlatformNotificationsCountByTypeType,
  GetUnacknowledgedPlatformNotificationsListType,
  PayloadForCreateEmailSubscription,
  PayloadForCreatePlatformSubscription,
  PayloadForUpdateCompanySubscriptionsParameters,
} from "./notificationsApi";
export interface NotificationsState {
  deleteEmailSubscriptionStatus: QueryStatus;
  deletePlatformSubscriptionStatus: QueryStatus;
  createPlatformSubscriptionStatus: QueryStatus;
  createEmailSubscriptionStatus: QueryStatus;
  getEmailSubscriptionsListStatus: QueryStatus;
  emailSubscriptionsList: GetEmailSubscriptionsListType[] | null;
  getPlatformSubscriptionsStatus: QueryStatus;
  platformSubscriptionsList: GetPlatformeSubscriptionsListType[] | null;
  getUnacknowledgedPlatformNotificationsStatus: QueryStatus;
  unacknowledgedPlatformNotificationsList:
    | GetUnacknowledgedPlatformNotificationsListType[]
    | null;
  getUnacknowledgedPlatformNotificationsCountByTypeStatus: QueryStatus;
  unacknowledgedPlatformNotificationsCountByType: GetUnacknowledgedPlatformNotificationsCountByTypeType | null;
  acknowledgePlatformNotificationStatus: QueryStatus;
  updateCompanySubscriptionsParametersStatus: QueryStatus;
}

const UNACKNOWLEDGED_PLATFORM_NOTIFICATIONS_LIMIT = 50;

const initialState: NotificationsState = {
  deleteEmailSubscriptionStatus: "idle",
  deletePlatformSubscriptionStatus: "idle",
  createPlatformSubscriptionStatus: "idle",
  createEmailSubscriptionStatus: "idle",
  getEmailSubscriptionsListStatus: "idle",
  emailSubscriptionsList: null,
  getPlatformSubscriptionsStatus: "idle",
  platformSubscriptionsList: null,
  getUnacknowledgedPlatformNotificationsStatus: "idle",
  unacknowledgedPlatformNotificationsList: null,
  getUnacknowledgedPlatformNotificationsCountByTypeStatus: "idle",
  unacknowledgedPlatformNotificationsCountByType: null,
  acknowledgePlatformNotificationStatus: "idle",
  updateCompanySubscriptionsParametersStatus: "idle",
};

export const deleteEmailSubscriptionAsync = createAsyncThunk(
  "notifications/deleteEmailSubscription",
  async (subscriptionUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `notifications/email_subscription/${subscriptionUuid}/delete`,
    );
    if (company) {
      void thunkAPI.dispatch(getEmailSubscriptionsListAsync(company.uuid));
    }
  },
);

export const createEmailSubscriptionAsync = createAsyncThunk(
  "notifications/createEmailSubscription",
  async (payload: PayloadForCreateEmailSubscription, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`notifications/email_subscription`, {
      company: payload.company,
      type: payload.type,
      role: payload.role,
      parameters: payload.parameters,
    });
    void thunkAPI.dispatch(getEmailSubscriptionsListAsync(payload.company));
  },
);

export const deletePlatformSubscriptionAsync = createAsyncThunk(
  "notifications/deletePlatformSubscription",
  async (subscriptionUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `notifications/platform_subscription/${subscriptionUuid}/delete`,
    );
    if (company) {
      void thunkAPI.dispatch(getPlatformSubscriptionsAsync(company.uuid));
    }
  },
);

export const createPlatformSubscriptionAsync = createAsyncThunk(
  "notifications/createPlatformSubscription",
  async (payload: PayloadForCreatePlatformSubscription, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;

    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`notifications/platform_subscription`, {
      company: payload.company,
      type: payload.type,
      parameters: payload.parameters,
    });
    void thunkAPI.dispatch(getPlatformSubscriptionsAsync(payload.company));
  },
);

export const updateCompanySubscriptionsParametersAsync = createAsyncThunk(
  "notifications/updateCompanySubscriptionsParameters",
  async (payload: PayloadForUpdateCompanySubscriptionsParameters) => {
    const axios = authAxios({ companyUuid: payload.company });

    await axios.post(`notifications/subscriptions/${payload.company}/update`, {
      company: payload.company,
      type: payload.type,
      parameters: payload.parameters,
    });
  },
);

export const getEmailSubscriptionsListAsync = createAsyncThunk(
  "notifications/getEmailSubscriptionsList",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<GetEmailSubscriptionsListType[]>(
      `notifications/email_subscriptions`,
      { params: { company: companyUuid } },
    );
    return response.data;
  },
);

export const getPlatformSubscriptionsAsync = createAsyncThunk(
  "notifications/getPlatformSubscriptions",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<GetPlatformeSubscriptionsListType[]>(
      `notifications/platform_subscriptions`,
      { params: { company: companyUuid } },
    );

    return response.data;
  },
);

export const acknowledgePlatformNotificationAsync = createAsyncThunk(
  "notifications/acknowledgePlatformNotification",
  async (notificationUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `notifications/platform_notification/${notificationUuid}/acknowledge`,
    );
  },
);

export const getUnacknowledgedPlatformNotificationsAsync = createAsyncThunk(
  "notifications/getUnacknowledgedPlatformNotifications",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<
      GetUnacknowledgedPlatformNotificationsListType[]
    >(`notifications/unacknowledged_platform_notifications`, {
      params: {
        company: companyUuid,
        limit: UNACKNOWLEDGED_PLATFORM_NOTIFICATIONS_LIMIT,
      },
    });
    return response.data.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const getUnacknowledgedPlatformNotificationsCountByTypeAsync =
  createAsyncThunk(
    "notifications/getUnacknowledgedPlatformNotificationsCountByType",
    async (companyUuid: string, thunkAPI) => {
      const company = (thunkAPI.getState() as RootState).home.company;
      const axios = authAxios({ companyUuid: company?.uuid });
      const response =
        await axios.get<GetUnacknowledgedPlatformNotificationsCountByTypeType>(
          `notifications/unacknowledged_platform_notifications/count`,
          { params: { company: companyUuid } },
        );
      return response.data;
    },
  );

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetNotificationsListStatus(state) {
      state.getUnacknowledgedPlatformNotificationsStatus = "idle";
      state.getUnacknowledgedPlatformNotificationsCountByTypeStatus = "idle";
      state.getEmailSubscriptionsListStatus = "idle";
      state.getPlatformSubscriptionsStatus = "idle";
    },
    resetEmailSubscriptionsListStatus(state) {
      state.getEmailSubscriptionsListStatus = "idle";
    },
    resetPlatformSubscriptionsListStatus(state) {
      state.getPlatformSubscriptionsStatus = "idle";
    },
    resetUpdateCompanySubscriptionsParametersStatus(state) {
      state.updateCompanySubscriptionsParametersStatus = "idle";
    },
    resetAcknowledgePlatformNotificationStatus(state) {
      state.acknowledgePlatformNotificationStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(deleteEmailSubscriptionAsync.pending, (state) => {
        state.deleteEmailSubscriptionStatus = "processing";
      })
      .addCase(deleteEmailSubscriptionAsync.fulfilled, (state) => {
        state.deleteEmailSubscriptionStatus = "success";
      })
      .addCase(deleteEmailSubscriptionAsync.rejected, (state) => {
        state.deleteEmailSubscriptionStatus = "failed";
      })
      .addCase(deletePlatformSubscriptionAsync.pending, (state) => {
        state.deletePlatformSubscriptionStatus = "processing";
      })
      .addCase(deletePlatformSubscriptionAsync.fulfilled, (state) => {
        state.deletePlatformSubscriptionStatus = "success";
      })
      .addCase(deletePlatformSubscriptionAsync.rejected, (state) => {
        state.deletePlatformSubscriptionStatus = "failed";
      })
      .addCase(createEmailSubscriptionAsync.pending, (state) => {
        state.createEmailSubscriptionStatus = "processing";
      })
      .addCase(createEmailSubscriptionAsync.fulfilled, (state) => {
        state.createEmailSubscriptionStatus = "success";
      })
      .addCase(createEmailSubscriptionAsync.rejected, (state) => {
        state.createEmailSubscriptionStatus = "failed";
      })
      .addCase(createPlatformSubscriptionAsync.pending, (state) => {
        state.createPlatformSubscriptionStatus = "processing";
      })
      .addCase(createPlatformSubscriptionAsync.fulfilled, (state) => {
        state.createPlatformSubscriptionStatus = "success";
      })
      .addCase(createPlatformSubscriptionAsync.rejected, (state) => {
        state.createPlatformSubscriptionStatus = "failed";
      })
      .addCase(getEmailSubscriptionsListAsync.pending, (state) => {
        state.getEmailSubscriptionsListStatus = "processing";
      })
      .addCase(getEmailSubscriptionsListAsync.fulfilled, (state, action) => {
        state.getEmailSubscriptionsListStatus = "success";
        state.emailSubscriptionsList = action.payload;
      })
      .addCase(getEmailSubscriptionsListAsync.rejected, (state) => {
        state.getEmailSubscriptionsListStatus = "failed";
      })
      .addCase(getPlatformSubscriptionsAsync.pending, (state) => {
        state.getPlatformSubscriptionsStatus = "processing";
      })
      .addCase(getPlatformSubscriptionsAsync.fulfilled, (state, action) => {
        state.getPlatformSubscriptionsStatus = "success";
        state.platformSubscriptionsList = action.payload;
      })
      .addCase(getUnacknowledgedPlatformNotificationsAsync.pending, (state) => {
        state.getUnacknowledgedPlatformNotificationsStatus = "processing";
      })
      .addCase(
        getUnacknowledgedPlatformNotificationsAsync.fulfilled,
        (state, action) => {
          state.getUnacknowledgedPlatformNotificationsStatus = "success";
          state.unacknowledgedPlatformNotificationsList = action.payload;
        },
      )
      .addCase(
        getUnacknowledgedPlatformNotificationsAsync.rejected,
        (state) => {
          state.getUnacknowledgedPlatformNotificationsStatus = "failed";
        },
      )
      .addCase(
        getUnacknowledgedPlatformNotificationsCountByTypeAsync.pending,
        (state) => {
          state.getUnacknowledgedPlatformNotificationsCountByTypeStatus =
            "processing";
        },
      )
      .addCase(
        getUnacknowledgedPlatformNotificationsCountByTypeAsync.fulfilled,
        (state, action) => {
          state.getUnacknowledgedPlatformNotificationsCountByTypeStatus =
            "success";
          state.unacknowledgedPlatformNotificationsCountByType = action.payload;
        },
      )
      .addCase(
        getUnacknowledgedPlatformNotificationsCountByTypeAsync.rejected,
        (state) => {
          state.getUnacknowledgedPlatformNotificationsCountByTypeStatus =
            "failed";
        },
      )
      .addCase(
        acknowledgePlatformNotificationAsync.pending,
        (state, action) => {
          state.acknowledgePlatformNotificationStatus = "processing";
          state.unacknowledgedPlatformNotificationsList =
            state.unacknowledgedPlatformNotificationsList?.filter(
              (notification) => notification.uuid !== action.meta.arg,
            ) ?? null;
        },
      )
      .addCase(acknowledgePlatformNotificationAsync.fulfilled, (state) => {
        state.acknowledgePlatformNotificationStatus = "success";
      })
      .addCase(acknowledgePlatformNotificationAsync.rejected, (state) => {
        state.acknowledgePlatformNotificationStatus = "failed";
      })
      .addCase(updateCompanySubscriptionsParametersAsync.pending, (state) => {
        state.updateCompanySubscriptionsParametersStatus = "processing";
      })
      .addCase(updateCompanySubscriptionsParametersAsync.fulfilled, (state) => {
        state.updateCompanySubscriptionsParametersStatus = "success";
        state.getPlatformSubscriptionsStatus = "idle";
        state.getEmailSubscriptionsListStatus = "idle";
      })
      .addCase(updateCompanySubscriptionsParametersAsync.rejected, (state) => {
        state.updateCompanySubscriptionsParametersStatus = "failed";
      });
  },
});

export const selectDeleteEmailSubscriptionStatus = (state: RootState) =>
  state.notifications.deleteEmailSubscriptionStatus;

export const selectDeletePlatformSubscriptionStatus = (state: RootState) =>
  state.notifications.deletePlatformSubscriptionStatus;

export const selectCreateEmailSubscriptionStatus = (state: RootState) =>
  state.notifications.createEmailSubscriptionStatus;

export const selectCreatePlatformSubscriptionStatus = (state: RootState) =>
  state.notifications.createPlatformSubscriptionStatus;

export const selectGetEmailSubscriptionsListStatus = (state: RootState) =>
  state.notifications.getEmailSubscriptionsListStatus;

export const selectEmailSubscriptionsList = (state: RootState) =>
  state.notifications.emailSubscriptionsList;

export const selectGetPlatformSubscriptionsStatus = (state: RootState) =>
  state.notifications.getPlatformSubscriptionsStatus;

export const selectPlatformSubscriptionsList = (state: RootState) =>
  state.notifications.platformSubscriptionsList;

export const selectUnacknowledgedPlatformNotificationsList = (
  state: RootState,
) => state.notifications.unacknowledgedPlatformNotificationsList;

export const selectGetUnacknowledgedPlatformNotificationsStatus = (
  state: RootState,
) => state.notifications.getUnacknowledgedPlatformNotificationsStatus;

export const selectUnacknowledgedPlatformNotificationsCountByType = (
  state: RootState,
) => state.notifications.unacknowledgedPlatformNotificationsCountByType;

export const selectGetUnacknowledgedPlatformNotificationsCountByTypeStatus = (
  state: RootState,
) =>
  state.notifications.getUnacknowledgedPlatformNotificationsCountByTypeStatus;

export const selectAcknowledgePlatformNotificationStatus = (state: RootState) =>
  state.notifications.acknowledgePlatformNotificationStatus;

export const selectUpdateCompanySubscriptionsParametersStatus = (
  state: RootState,
) => state.notifications.updateCompanySubscriptionsParametersStatus;

export default notificationsSlice.reducer;
